import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';

/** Viewport width below which the drawer opens as a modal overlay (no squeeze). */
const MODAL_BREAKPOINT = 990;

/**
 * IntactPlus: rubber-band ca pe iOS — rezistenta creste cu cat tragi mai mult
 * in directia gresita. offset = (1 - 1 / (c * x / d + 1)) * d
 *
 * @param {number} distance - Cat a tras degetul (px).
 * @param {number} dimension - Distanta maxima spre care tinde panoul (px).
 * @returns {number}
 */
function rubberBand(distance, dimension) {
  return (1 - 1 / ((0.55 * distance) / dimension + 1)) * dimension;
}

/**
 * IntactPlus: pozitiile (la 60fps) ale unui arc fara bounce de la `from` la `to`,
 * care porneste cu viteza degetului, ca animatia sa continue miscarea, nu sa o reia.
 *
 * @param {number} from - Pozitia de start (px).
 * @param {number} to - Pozitia finala (px).
 * @param {number} velocity - Viteza initiala (px/s).
 * @param {number} response - Cat de rapid e arcul (s); mai mic = mai rapid.
 * @returns {number[]}
 */
function springFrames(from, to, velocity, response) {
  const omega = (2 * Math.PI) / response;
  const a = from - to;
  const b = velocity + omega * a;
  const frames = [from];

  for (let i = 1; i <= 60; i++) {
    const t = i / 60;
    const decay = Math.exp(-omega * t);
    const x = to + (a + b * t) * decay;
    const v = (velocity - omega * b * t) * decay;
    frames.push(x);
    if (Math.abs(x - to) < 1 && Math.abs(v) < 20) break;
  }

  frames[frames.length - 1] = to;
  return frames;
}

/**
 * A drawer that opens from the right side.
 *
 * On wide viewports (≥ 990px) the drawer squeezes page content alongside it.
 * The panel is a non-modal dialog (`show()`); we install a focus trap via
 * `trapFocus()` so Tab cycles within the drawer, mirroring the modal-mode
 * a11y contract. Focus moves to the close button on open and returns to
 * the trigger on close.
 *
 * On narrow viewports (< 990px) the drawer overlays with a backdrop. The
 * panel is a modal dialog (`showModal()`) — native focus trap, scroll-lock,
 * and ARIA semantics. Same focus-on-close-button + restore-on-close UX.
 *
 * Dispatches {@link DrawerOpenEvent} and {@link DrawerCloseEvent}.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} panel - The dialog that owns all visual styles and animation.
 *
 * @extends {Component<Refs>}
 */
export class ThemeDrawer extends Component {
  requiredRefs = ['panel'];

  /** Monotonically increasing counter for z-index stacking of multiple open drawers. */
  static #stackOrder = 0;

  /** @type {boolean} */
  #isClosing = false;

  /** When true, the drawer will reopen after the current close animation finishes. */
  /** @type {{ enter?: 'inline-start' } | false} */
  #deferredOpen = false;

  /** Element to restore focus to when the drawer closes. */
  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  /** @type {MediaQueryList} */
  #modalQuery = window.matchMedia(`(max-width: ${MODAL_BREAKPOINT - 1}px)`);

  /** IntactPlus: starea gestului swipe-to-close (doar pe mobil, in modul modal). */
  /** @type {{ id: number, startX: number, startY: number, base: number, axis: 'x' | 'y' | null, offset: number, samples: { x: number, t: number }[] } | null} */
  #swipe = null;

  /** @type {Animation | null} */
  #swipeAnimation = null;

  /** True cat timp panoul aluneca afara dupa un swipe. */
  #swipeClosing = false;

  /** Dupa un swipe, atingerea nu trebuie sa devina click pe ce era sub deget. */
  #suppressClickUntil = 0;

  #reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  /**
   * @returns {boolean} Whether the drawer is currently open.
   */
  get isOpen() {
    return this.hasAttribute('open');
  }

  connectedCallback() {
    super.connectedCallback();
    this.#modalQuery.addEventListener('change', this.#onModalBreakpointChange);
    this.addEventListener('pointerdown', this.#onSwipeStart);
    this.addEventListener('pointermove', this.#onSwipeMove);
    this.addEventListener('pointerup', this.#onSwipeEnd);
    this.addEventListener('pointercancel', this.#onSwipeEnd);
    this.addEventListener('click', this.#onClickAfterSwipe, { capture: true });

    // Sync the static stack counter with any --drawer-stack-order set by the
    // synchronous restore script (which runs before this module loads).
    const restoredOrder = Number(this.style.getPropertyValue('--drawer-stack-order') || 0);
    if (restoredOrder > ThemeDrawer.#stackOrder) {
      ThemeDrawer.#stackOrder = restoredOrder;
    }

    if (this.isOpen) {
      this.#onRestore();
    }
  }

  /**
   * Restore path: the inline script in theme-drawer.liquid set [open] on
   * <theme-drawer> + <dialog> before this module loaded. The dialog is
   * already visible — we just wire close listeners. We deliberately skip
   * trapFocus and focus moves: the user is loading a fresh page and
   * expects focus on main content, not inside a drawer left over from
   * the previous session.
   */
  #onRestore() {
    const { panel } = this.refs;
    if (this.#modalQuery.matches) {
      lockScroll(panel);
    }

    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    unlockScroll(this.refs.panel);
    this.#modalQuery.removeEventListener('change', this.#onModalBreakpointChange);
    this.removeEventListener('pointerdown', this.#onSwipeStart);
    this.removeEventListener('pointermove', this.#onSwipeMove);
    this.removeEventListener('pointerup', this.#onSwipeEnd);
    this.removeEventListener('pointercancel', this.#onSwipeEnd);
    this.removeEventListener('click', this.#onClickAfterSwipe, { capture: true });
    this.#swipe = null;
    this.#swipeAnimation?.cancel();
    this.#removeEventListeners();
    removeTrapFocus();
    this.#deferredOpen = false;
  }

  /**
   * IntactPlus: incepe un posibil swipe. Directia (orizontal / vertical) se decide
   * abia la prima miscare, ca scroll-ul vertical din panou sa ramana nativ.
   *
   * @param {PointerEvent} event
   */
  #onSwipeStart = (event) => {
    if (event.pointerType !== 'touch' || !event.isPrimary) return;
    if (!this.isOpen || this.#isClosing || this.#swipeClosing || !this.#modalQuery.matches) return;

    const target = event.target instanceof Element ? event.target : null;
    if (!target || !this.refs.panel.contains(target)) return;
    if (target.closest('input, textarea, select, [contenteditable]')) return;
    if (this.#hasOpenNestedDialog()) return;

    this.#swipe = {
      id: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      base: 0,
      axis: null,
      offset: 0,
      samples: [],
    };
  };

  /**
   * @param {PointerEvent} event
   */
  #onSwipeMove = (event) => {
    const swipe = this.#swipe;
    if (!swipe || event.pointerId !== swipe.id) return;

    if (!swipe.axis) {
      const dx = event.clientX - swipe.startX;
      const dy = event.clientY - swipe.startY;
      if (Math.hypot(dx, dy) < 8) return;

      swipe.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (swipe.axis === 'y') {
        this.#swipe = null;
        return;
      }

      // Daca panoul inca revine dintr-un swipe anterior, il prindem din mers.
      swipe.base = this.#takeOverSwipeAnimation();
      swipe.startX = event.clientX;
    }

    const { panel } = this.refs;
    const raw = swipe.base + event.clientX - swipe.startX;
    // Spre dreapta (inchidere) panoul urmeaza degetul 1:1; spre stanga opune rezistenta.
    swipe.offset = raw >= 0 ? raw : -rubberBand(-raw, panel.offsetWidth * 0.15);
    panel.style.transform = `translateX(${swipe.offset}px)`;

    swipe.samples.push({ x: swipe.offset, t: event.timeStamp });
    while (swipe.samples.length > 2 && event.timeStamp - swipe.samples[0].t > 100) {
      swipe.samples.shift();
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #onSwipeEnd = (event) => {
    const swipe = this.#swipe;
    if (!swipe || event.pointerId !== swipe.id) return;
    this.#swipe = null;
    if (swipe.axis !== 'x') return;

    this.#suppressClickUntil = event.timeStamp + 400;

    const width = this.refs.panel.offsetWidth;
    const first = swipe.samples[0];
    const last = swipe.samples[swipe.samples.length - 1];
    const elapsed = first && last ? last.t - first.t : 0;
    const velocity = elapsed > 0 ? ((last.x - first.x) / elapsed) * 1000 : 0;

    // Ca pe iOS: unde s-ar opri panoul daca ar continua cu viteza de acum.
    const projected = swipe.offset + velocity * 0.2;

    if (event.type === 'pointerup' && projected > width / 2) {
      this.#swipeClose(swipe.offset, velocity, width);
    } else {
      this.#animateOffset(springFrames(swipe.offset, 0, velocity, 0.38));
    }
  };

  /**
   * @param {MouseEvent} event
   */
  #onClickAfterSwipe = (event) => {
    if (event.timeStamp < this.#suppressClickUntil) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.#suppressClickUntil = 0;
  };

  /**
   * @returns {number} Pozitia curenta a panoului (px), oprind animatia in curs.
   */
  #takeOverSwipeAnimation() {
    if (!this.#swipeAnimation) return 0;

    const offset = new DOMMatrix(getComputedStyle(this.refs.panel).transform).m41;
    this.#swipeAnimation.cancel();
    this.#swipeAnimation = null;
    return offset;
  }

  /**
   * Anima panoul prin pozitiile date si il lasa in ultima.
   *
   * @param {number[]} frames
   * @returns {Promise<void>}
   */
  async #animateOffset(frames) {
    const { panel } = this.refs;
    const final = frames[frames.length - 1];
    panel.style.transform = final ? `translateX(${final}px)` : '';

    if (this.#reducedMotionQuery.matches || frames.length < 2) return;

    const animation = panel.animate(
      frames.map((x) => ({ transform: `translateX(${x}px)` })),
      { duration: ((frames.length - 1) * 1000) / 60, easing: 'linear' }
    );
    this.#swipeAnimation = animation;

    try {
      await animation.finished;
    } catch {
      // Anulata (degetul a prins panoul din nou) — e in regula.
    } finally {
      if (this.#swipeAnimation === animation) this.#swipeAnimation = null;
    }
  }

  /**
   * Panoul continua miscarea degetului pana iese din ecran, apoi drawer-ul se inchide
   * fara a mai rula animatia lui de inchidere.
   *
   * @param {number} offset
   * @param {number} velocity
   * @param {number} width
   */
  async #swipeClose(offset, velocity, width) {
    this.#swipeClosing = true;
    const { panel } = this.refs;

    await this.#animateOffset(springFrames(offset, width, Math.max(velocity, 0), 0.3));

    panel.classList.add('theme-drawer__dialog-instant');
    await this.close();
    panel.classList.remove('theme-drawer__dialog-instant');
    panel.style.transform = '';
    this.#swipeClosing = false;
  }

  /**
   * Closes the drawer when the user clicks the backdrop (outside the dialog
   * content area). Only meaningful in modal mode — in sidebar mode the
   * dialog has no backdrop so this handler is inert.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #onBackdropClick = (event) => {
    const { panel } = this.refs;

    if (isClickedOutside(event, panel)) {
      if (this.#hasOpenNestedDialog()) return;

      this.close();
    }
  };

  /**
   * @returns {boolean} Whether the drawer panel contains an open dialog other than itself.
   */
  #hasOpenNestedDialog() {
    return this.#getOpenNestedDialog() !== null;
  }

  /**
   * @returns {HTMLDialogElement | null} The open dialog nested inside the drawer panel, if any.
   */
  #getOpenNestedDialog() {
    return /** @type {HTMLDialogElement | null} */ (this.refs.panel.querySelector('dialog[open]'));
  }

  /**
   * Switches the dialog between modal and non-modal when the viewport
   * crosses the modal breakpoint while the drawer is open.
   */
  #onModalBreakpointChange = () => {
    if (!this.isOpen) return;

    const { panel } = this.refs;
    const nestedDialog = this.#getOpenNestedDialog();
    const nestedActiveElement =
      nestedDialog?.contains(document.activeElement) && document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    // Close the current dialog mode and immediately reopen in the new mode.
    // No animation — the drawer stays visually in place.
    panel.close();
    removeTrapFocus();

    if (this.#modalQuery.matches) {
      lockScroll(panel);
      panel.showModal();
    } else {
      unlockScroll(panel);
      panel.show();
      trapFocus(panel);
    }

    if (nestedDialog?.open) {
      this.#bringNestedDialogToFront(nestedDialog, nestedActiveElement);
    } else {
      this.#focusInitialElement();
    }

    // Ensure keydown and click listeners are registered. The sessionStorage
    // restore path bypasses open(), so these may not have been added yet.
    // addEventListener deduplicates identical listeners, so this is safe
    // even if they were already registered.
    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  };

  /**
   * Closes the drawer when the user presses Escape.
   * preventDefault() suppresses the native dialog cancel event in modal mode.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    // isComposing: don't close while an Input Method Editor (e.g. Japanese,
    // Chinese, Korean input) consumes Escape to dismiss its candidate list —
    // can happen inside the cart's discount-code field.
    // defaultPrevented: let nested components handle Escape first.
    if (event.isComposing || event.defaultPrevented) return;
    if (!this.isOpen) return;
    if (!this.#isTopmost()) return;

    event.preventDefault();
    this.close();
  };

  /**
   * Shows the drawer.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter] - Slide direction. `'inline-start'` slides left-to-right (used when the chat docks).
   */
  open({ enter } = {}) {
    if (this.isOpen) {
      this.#bringToFront({ enter });
      return;
    }
    if (this.#isClosing) {
      this.#deferredOpen = { enter };
      return;
    }

    const { panel } = this.refs;

    ThemeDrawer.#stackOrder += 1;
    this.style.setProperty('--drawer-stack-order', String(ThemeDrawer.#stackOrder));

    const openClass =
      enter === 'inline-start' ? 'theme-drawer__dialog--opening-inline-start' : 'theme-drawer__dialog--opening';
    panel.classList.add(openClass);

    this.#previouslyFocused = /** @type {HTMLElement | null} */ (document.activeElement);

    if (this.#modalQuery.matches) {
      lockScroll(panel);
      panel.showModal();
    } else {
      panel.show();
      trapFocus(panel);
    }

    this.#focusInitialElement();

    onAnimationEnd(panel, () => panel.classList.remove(openClass), { subtree: false });

    this.setAttribute('open', '');
    this.dispatchEvent(new DrawerOpenEvent());

    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  }

  /**
   * Bumps this drawer's z-index to the top of the stack and replays
   * the slide-in animation. Called when open() is invoked on an
   * already-open drawer.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter] - Slide direction.
   */
  #bringToFront({ enter } = {}) {
    const { panel } = this.refs;

    ThemeDrawer.#stackOrder += 1;
    this.style.setProperty('--drawer-stack-order', String(ThemeDrawer.#stackOrder));

    // In modal mode, dialogs live in the browser's top layer where z-index
    // is ignored — stacking follows showModal() call order. Re-calling
    // showModal() moves this dialog to the top of the stack.
    if (this.#modalQuery.matches && panel.open) {
      lockScroll(panel);
      panel.close();
      panel.showModal();
    }

    const openClass =
      enter === 'inline-start' ? 'theme-drawer__dialog--opening-inline-start' : 'theme-drawer__dialog--opening';
    panel.classList.add(openClass);
    onAnimationEnd(panel, () => panel.classList.remove(openClass), { subtree: false });

    // Re-fire so persistDrawerState picks up the new stack order.
    this.dispatchEvent(new DrawerOpenEvent());
  }

  #removeEventListeners() {
    const { panel } = this.refs;

    document.removeEventListener('keydown', this.#onKeyDown);
    panel.removeEventListener('click', this.#onBackdropClick);
  }

  /**
   * Moves focus to the close button inside the drawer panel.
   * Every theme-drawer renders the close button via theme-drawer-header.
   * If a future drawer omits it, that drawer is responsible for its own focus management.
   */
  #focusInitialElement() {
    const closeButton = /** @type {HTMLElement | null} */ (
      this.refs.panel.querySelector('.theme-drawer__close-button')
    );
    closeButton?.focus();
  }

  /**
   * Reopens a nested modal after the drawer panel changes mode so it remains
   * above the drawer in the browser top layer.
   *
   * @param {HTMLDialogElement} dialog - The nested dialog to restack.
   * @param {HTMLElement | null} focusTarget - Element to refocus inside the nested dialog.
   */
  #bringNestedDialogToFront(dialog, focusTarget) {
    dialog.close();
    dialog.showModal();

    if (focusTarget && document.contains(focusTarget) && dialog.contains(focusTarget)) {
      focusTarget.focus();
    }
  }

  /**
   * Slides the drawer out, waits for the animation, then closes the dialog.
   */
  async #closeDialog() {
    if (!this.isOpen) return;
    if (this.#isClosing) return;
    this.#isClosing = true;

    this.#removeEventListeners();
    removeTrapFocus();

    const { panel } = this.refs;

    this.removeAttribute('open');
    this.dispatchEvent(new DrawerCloseEvent());

    unlockScroll(panel);

    if (panel.open) {
      // Cancel any in-progress open animation before starting the close.
      panel.classList.remove('theme-drawer__dialog--opening', 'theme-drawer__dialog--opening-inline-start');

      panel.classList.add('theme-drawer__dialog--closing');
      await onAnimationEnd(panel, undefined, { subtree: false });
      panel.classList.remove('theme-drawer__dialog--closing');
    }

    panel.close();
    this.style.removeProperty('--drawer-stack-order');

    if (!document.querySelector('theme-drawer[open]')) {
      ThemeDrawer.#stackOrder = 0;
    }

    const trigger = this.#previouslyFocused;
    this.#previouslyFocused = null;
    // A Section Rendering API morph between open and close can replace the
    // trigger node. The JS reference stays valid but the element is detached
    // from the live DOM — and .focus() on a detached node is a silent no-op,
    // so we explicitly check and fall back to a fresh query.
    if (trigger && document.contains(trigger)) {
      trigger.focus();
    } else {
      /** @type {HTMLElement | null} */ (document.querySelector(`[aria-controls="${this.id}"]`))?.focus();
    }

    this.#isClosing = false;

    // Reconcile: if open() was called during the close animation, reopen now.
    if (this.#deferredOpen) {
      const options = this.#deferredOpen;
      this.#deferredOpen = false;
      this.open(options);
    }
  }

  /**
   * Closes the drawer.
   */
  async close() {
    if (this.#isClosing) {
      // A close is already in progress — cancel any deferred open so the
      // drawer stays closed when the animation finishes.
      this.#deferredOpen = false;
      return;
    }
    await this.#closeDialog();
  }

  /**
   * Toggles the drawer. If the drawer is open but not the topmost in the
   * stack, it is brought to front instead of closed.
   */
  toggle() {
    if (this.isOpen && this.#isTopmost()) {
      return this.close();
    }
    return this.open();
  }

  /**
   * @returns {boolean} Whether this drawer has the highest stack order among all open drawers.
   */
  #isTopmost() {
    const myOrder = Number(this.style.getPropertyValue('--drawer-stack-order') || 0);
    for (const other of document.querySelectorAll('theme-drawer[open]')) {
      if (other === this) continue;
      const otherOrder = Number(/** @type {HTMLElement} */ (other).style.getPropertyValue('--drawer-stack-order') || 0);
      if (otherOrder > myOrder) return false;
    }
    return true;
  }
}

if (!customElements.get('theme-drawer')) {
  customElements.define('theme-drawer', ThemeDrawer);
}

export class DrawerOpenEvent extends CustomEvent {
  constructor() {
    super(DrawerOpenEvent.eventName, { bubbles: true });
  }

  static eventName = 'theme-drawer:open';
}

export class DrawerCloseEvent extends CustomEvent {
  constructor() {
    super(DrawerCloseEvent.eventName, { bubbles: true });
  }

  static eventName = 'theme-drawer:close';
}